import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/authService';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface PasswordForm {
   currentPassword: string;
   newPassword: string;
   confirmPassword: string;
}

export const ChangePasswordPage = () => {
   const { user, setUser } = useAuth();
   const navigate = useNavigate();
   const [isLoading, setIsLoading] = useState(false);

   const {
      register,
      handleSubmit,
      watch,
      formState: { errors },
   } = useForm<PasswordForm>();

   const newPassword = watch('newPassword');
   const isFirstLogin = user?.isFirstLogin || user?.requiresPasswordChange;

   const onSubmit = async (data: PasswordForm) => {
      try {
         setIsLoading(true);

         await authService.changePassword(data);

         // Update user state to reflect password change
         if (user) {
            setUser({
               ...user,
               isFirstLogin: false,
               requiresPasswordChange: false,
            });
         }

         toast.success('Password changed successfully!');
         navigate('/');
      } catch (error: any) {
         const errorMessage =
            error.response?.data?.error || error.message || 'Failed to change password';
         toast.error(errorMessage);
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
         <div className='max-w-md w-full space-y-8'>
            <div>
               <img className='mx-auto h-12 w-auto' src='/logo.svg' alt='WeTrippen' />
               <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
                  {isFirstLogin ? 'Set Your Password' : 'Change Password'}
               </h2>
               <p className='mt-2 text-center text-sm text-gray-600'>
                  {isFirstLogin
                     ? 'Create a secure password for your admin account'
                     : 'Update your current password'}
               </p>
            </div>

            {/* User Info Card */}
            {user && (
               <div className='bg-gray-50 border border-gray-200 rounded-lg p-4'>
                  <div className='flex items-center'>
                     <div className='flex-shrink-0'>
                        <div className='h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center'>
                           <span className='text-primary-600 font-medium text-sm'>
                              {user.firstName?.charAt(0) || user.email.charAt(0).toUpperCase()}
                           </span>
                        </div>
                     </div>
                     <div className='ml-3'>
                        <p className='text-sm font-medium text-gray-900'>
                           {user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.email}
                        </p>
                        <p className='text-xs text-gray-500'>{user.email}</p>
                        <p className='text-xs text-gray-500'>
                           Role: <span className='capitalize font-medium'>{user.role}</span>
                        </p>
                     </div>
                  </div>
               </div>
            )}

            {/* Security Notice */}
            {isFirstLogin && (
               <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
                  <div className='flex'>
                     <div className='flex-shrink-0'>
                        <svg
                           className='h-5 w-5 text-yellow-400'
                           viewBox='0 0 20 20'
                           fill='currentColor'
                        >
                           <path
                              fillRule='evenodd'
                              d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                              clipRule='evenodd'
                           />
                        </svg>
                     </div>
                     <div className='ml-3'>
                        <h3 className='text-sm font-medium text-yellow-800'>
                           Password Setup Required
                        </h3>
                        <div className='mt-2 text-sm text-yellow-700'>
                           <p>Please create a secure password to complete your account setup.</p>
                        </div>
                     </div>
                  </div>
               </div>
            )}

            <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
               <div className='space-y-4'>
                  {/* Current Password */}
                  <div>
                     <label
                        htmlFor='currentPassword'
                        className='block text-sm font-medium text-gray-700'
                     >
                        {isFirstLogin ? 'Temporary Password' : 'Current Password'}
                     </label>
                     <input
                        {...register('currentPassword', {
                           required: 'Current password is required',
                        })}
                        id='currentPassword'
                        name='currentPassword'
                        type='text'
                        autoComplete='current-password'
                        className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                           errors.currentPassword ? 'border-red-300' : 'border-gray-300'
                        } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                        placeholder={
                           isFirstLogin
                              ? 'Enter your temporary password'
                              : 'Enter your current password'
                        }
                     />
                     {errors.currentPassword && (
                        <p className='mt-1 text-sm text-red-600'>
                           {errors.currentPassword.message}
                        </p>
                     )}
                  </div>

                  {/* New Password */}
                  <div>
                     <label
                        htmlFor='newPassword'
                        className='block text-sm font-medium text-gray-700'
                     >
                        New Password
                     </label>
                     <input
                        {...register('newPassword', {
                           required: 'New password is required',
                           minLength: {
                              value: 8,
                              message: 'Password must be at least 8 characters long',
                           },
                        })}
                        id='newPassword'
                        name='newPassword'
                        type='password'
                        autoComplete='new-password'
                        className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                           errors.newPassword ? 'border-red-300' : 'border-gray-300'
                        } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                        placeholder='Enter your new password'
                     />
                     {errors.newPassword && (
                        <p className='mt-1 text-sm text-red-600'>{errors.newPassword.message}</p>
                     )}
                     <div className='mt-1 text-xs text-gray-500'>
                        <p>Password requirements:</p>
                        <ul className='list-disc list-inside mt-1 space-y-1'>
                           <li>At least 8 characters long</li>
                           <li>Mix of letters and numbers recommended</li>
                        </ul>
                     </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                     <label
                        htmlFor='confirmPassword'
                        className='block text-sm font-medium text-gray-700'
                     >
                        Confirm New Password
                     </label>
                     <input
                        {...register('confirmPassword', {
                           required: 'Please confirm your password',
                           validate: (value) => value === newPassword || 'Passwords do not match',
                        })}
                        id='confirmPassword'
                        name='confirmPassword'
                        type='password'
                        autoComplete='new-password'
                        className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                           errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                        } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                        placeholder='Confirm your new password'
                     />
                     {errors.confirmPassword && (
                        <p className='mt-1 text-sm text-red-600'>
                           {errors.confirmPassword.message}
                        </p>
                     )}
                  </div>
               </div>

               <div>
                  <button
                     type='submit'
                     disabled={isLoading}
                     className='group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                     {isLoading ? (
                        <>
                           <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                           {isFirstLogin ? 'Setting up password...' : 'Changing password...'}
                        </>
                     ) : (
                        <>{isFirstLogin ? 'Complete Setup' : 'Change Password'}</>
                     )}
                  </button>
               </div>

               <div className='text-center'>
                  <p className='text-xs text-gray-500'>
                     {isFirstLogin
                        ? "After setting your password, you'll have full access to the admin dashboard"
                        : 'Your new password will be required for future logins'}
                  </p>
               </div>
            </form>
         </div>
      </div>
   );
};
